/**
 * friendlyError(err, fallback)
 *
 * Turns any thrown error (Firebase error codes, raw JS exceptions, strings)
 * into a clean, human sentence safe to show a teacher at a primary school.
 *
 * NEVER leaks code jargon: no raw err.message with codes, no "JSON",
 * no "undefined"/"null", no "storage/unauthorized", no "Firebase:",
 * no "permission-denied", no stack traces, no "console".
 *
 * Usage:
 *   catch (err) { showToast(friendlyError(err, "Couldn't save your changes. Please try again.")); }
 */

const GENERIC = 'Something went wrong. Please try again.';

// Ordered list of matchers. Each entry: [regex tested against the lowercased
// raw text, friendly replacement sentence]. First match wins.
const MAP = [
  // Stale code-split chunk after a deploy — the loaded page references chunk
  // filenames that were replaced. A refresh fixes it.
  [/failed to fetch dynamically imported module|importing a module script failed|module script failed|loading chunk\s+\S+\s+failed|chunkloaderror/,
    'A newer version is available - please refresh the page to load this section.'],
  // Permissions
  [/permission[-\s]?denied|missing or insufficient permissions|storage\/unauthorized|unauthorized|not allowed|insufficient permission/,
    "You don't have permission to do that."],
  // Auth / session
  [/unauthenticated|auth\/|not signed in|sign[-\s]?in|login|requires? recent|token[-\s]?expired|user[-\s]?token/,
    'Please sign in again.'],
  // Scanned / image-only document — no text layer to read. VERY common for
  // OT / psych / speech reports (scanned or photographed). Must come BEFORE the
  // generic AI/connectivity fallbacks so it isn't mis-reported as "AI
  // unavailable" (which is what made staff think the assistant was broken).
  [/could not (extract|read)[^.]*text|no readable text|no text (layer|found|to read)|scanned image|image[-\s]?only|selectable text|needs? to be searchable/,
    'This looks like a scanned or photographed document, so there\'s no readable text in it to summarise. Upload a version with selectable text (or the original Word/PDF export) and try again.'],
  // Couldn't download the file itself — expired storage link or a transient
  // storage hiccup. Also previously fell through to the generic AI message.
  [/fetch failed \(\d+\)|allowlist validation|could not (download|open)[^.]*(file|document)/,
    'Couldn\'t open that file to read it. Close and re-open the student, then try again - the file link may have expired.'],
  // Connectivity
  [/unavailable|failed to fetch|network|deadline[-\s]?exceeded|networkerror|econnrefused|timeout|timed out|offline|net::/,
    'Connection problem - please check your internet and try again.'],
  // Quota / rate limits (there is NO daily cap — be accurate per cause).
  // Per-user hourly cap.
  [/hourly ai request limit|hourly.*limit reached/,
    "You've reached the hourly AI limit. Please try again a bit later."],
  // Per-user per-minute burst guard.
  [/in the last minute|slow down a touch/,
    'That was a lot of AI requests at once - give it a moment and try again.'],
  // Everything else quota-ish (raw 429s from the AI service, generic rate limits).
  [/quota|resource[-\s]?exhausted|rate[-\s]?limit|too many requests|429/,
    'The AI service is busy right now. Please try again in a few minutes.'],
  // File too large / storage size
  [/exceeds|too large|payload|entity too large|file size|maximum.*size|size.*maximum|413|storage\/quota/,
    'That file is too large.'],
  // Not found
  [/not[-\s]?found|no such|does not exist|doesn't exist|404/,
    "That item couldn't be found. It may have been removed."],
  // Already exists / conflict
  [/already[-\s]?exists|conflict|aborted/,
    'That was already saved, or someone else changed it. Please refresh and try again.'],
];

/**
 * Extract the best raw text we can from an unknown thrown value.
 */
function rawText(err) {
  if (err == null) return '';
  if (typeof err === 'string') return err;
  // Firebase errors put the machine code on .code and a message on .message.
  const parts = [];
  if (typeof err.code === 'string') parts.push(err.code);
  if (typeof err.message === 'string') parts.push(err.message);
  if (parts.length) return parts.join(' ');
  try {
    return String(err);
  } catch {
    return '';
  }
}

/**
 * Heuristic: is this message already a clean, human, jargon-free sentence we
 * can safely show as-is?
 */
function isCleanSentence(msg) {
  if (typeof msg !== 'string') return false;
  const s = msg.trim();
  if (!s) return false;
  if (s.length > 120) return false;
  // Must start with a capital letter (reads like a sentence).
  if (!/^[A-Z]/.test(s)) return false;
  // No code-style tokens.
  if (/undefined|null|\bNaN\b/.test(s)) return false;
  if (/json/i.test(s)) return false;
  if (/console|stack|traceback|typeerror|referenceerror|syntaxerror/i.test(s)) return false;
  if (/firebase|firestore|gapi|xhr|http[s]?:\/\//i.test(s)) return false;
  // No "code/path" style tokens like auth/x, storage/x, permission-denied.
  if (/\b[a-z]+\/[a-z-]+\b/.test(s)) return false;
  if (/-denied|-exhausted|-exceeded|-not-found/.test(s)) return false;
  // No "Error:" / "Failed: <code>" leaks or leading label colons.
  if (/error\s*:/i.test(s)) return false;
  if (/:\s*[a-z]/.test(s)) return false; // "something: lowercase" reads like a code dump
  // No braces / brackets / angle brackets (object dumps, HTML).
  if (/[{}[\]<>]/.test(s)) return false;
  return true;
}

export function friendlyError(err, fallback) {
  const generic = typeof fallback === 'string' && fallback.trim()
    ? fallback.trim()
    : GENERIC;

  const raw = rawText(err);
  const lower = raw.toLowerCase();

  // School-level AI capacity messages come from our own Cloud Function and name
  // who to contact to lift the cap — surface them verbatim rather than a
  // generic line. (These are the annual budget + monthly safety caps.)
  if (/allocated capacity|year'?s? allocated|safety cap/.test(lower)) {
    const serverMsg = (err && typeof err.message === 'string') ? err.message.trim() : '';
    if (serverMsg) return serverMsg;
  }

  for (const [re, friendly] of MAP) {
    if (re.test(lower)) return friendly;
  }

  // If it's already a clean human sentence, pass it through.
  const msg = (err && typeof err.message === 'string') ? err.message
    : (typeof err === 'string' ? err : '');
  if (isCleanSentence(msg)) return msg.trim();

  return generic;
}

export default friendlyError;
